"use client"

import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { userSelect } from "../../../redux/selectors/index"
import { notify } from "../../../utility/Helper1"
import { authActions } from "../../../redux/actions"
import PinAuth from "../../../utility/customComponents/PinAuth"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import { EyeIcon, EyeOffIcon, KeyIcon, ShieldCheckIcon } from "lucide-react"

const ChangePassword = () => {
  const dispatch = useDispatch()
  const userData = useSelector(userSelect)
  const [password, setPassword] = useState("")
  const [password2, setPassword2] = useState("")
  const [securityObj, setSecurityObj] = useState({})
  const [passwordUpdate, setPasswordUpdate] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const doPasswordChange = async (e) => {
    if (passwordUpdate) {
      return
    }
    e.preventDefault()

    if (password === "") {
      notify("Please input new password", 0)
      return false
    }

    if (password !== password2) {
      notify("Password mismatch. Enter valid passwords", 0)
      return false
    }

    const data = { password, ...(securityObj ?? {}) }

    setPasswordUpdate(true)

    dispatch(
      authActions.onLogin("account/change_password", data, (response) => {
        if (response.code == 1) {
          setSecurityObj({})
          setPassword("")
          setPassword2("")
        }
        notify(response.msg, response.code)
        setPasswordUpdate(false)
      }),
    )
  }

  return (
    <WrapUpP2p pageTitle={"Change Password"}>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center">
              <KeyIcon className="h-5 w-5 text-indigo-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Change Password</h3>
            </div>
            <p className="mt-1 text-sm text-gray-500">Update your password to keep your account secure</p>
          </div>

          <div className="p-6">
            <form onSubmit={doPasswordChange}>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-100 pt-6">
                <div className="flex items-center mb-2">
                  <ShieldCheckIcon className="h-5 w-5 text-indigo-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-700">Security Verification</h4>
                </div>
                <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={passwordUpdate}
                  className={`w-full md:w-auto px-6 py-3 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all ${passwordUpdate ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {passwordUpdate ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    "Update Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </WrapUpP2p>
  )
}

export default ChangePassword
